// Validate form data against a zod schema, eagerly and without classic zod.
// Adapted from @conform-to/zod 1.19.4 `parseWithZod` (MIT).
import { parse, type Submission } from '@conform-to/react';
import {
  type $ZodIssue,
  type $ZodType,
  type output,
  safeParse,
  safeParseAsync,
} from 'zod/v4/core';

import { coerceFormValue } from './coerce';
import { formatPaths } from './paths';

// Issue messages Conform's zod integration gives a meaning to. Kept so a
// schema written against it behaves the same.
const VALIDATION_SKIPPED = '__skipped__';
const VALIDATION_UNDEFINED = '__undefined__';

type FieldErrors = Record<string, string[] | null>;

const getError = (issues: $ZodIssue[]): FieldErrors | null => {
  const grouped: Record<string, $ZodIssue[] | null> = {};
  for (const issue of issues) {
    const name = formatPaths(
      issue.path.map((segment) => {
        if (typeof segment === 'symbol') {
          throw new Error('Form schemas cannot use symbol paths');
        }
        return segment;
      }),
    );
    if (issue.message === VALIDATION_UNDEFINED) return null;
    if (issue.message === VALIDATION_SKIPPED) {
      grouped[name] = null;
    } else if (grouped[name] !== null) {
      grouped[name] = [...(grouped[name] ?? []), issue];
    }
  }
  return Object.fromEntries(
    Object.entries(grouped).map(([name, list]) => [
      name,
      list ? list.map((issue) => issue.message) : null,
    ]),
  );
};

interface ParseOptions<S extends $ZodType> {
  schema: S;
  /** Use `true` when the schema has async refinements. */
  async?: boolean;
}

/**
 * Parse `FormData` or `URLSearchParams` into a Conform submission: the values
 * are coerced to what the schema expects (see {@link coerceFormValue}) and
 * validated, and each issue becomes an error on the field it names.
 *
 * Works with schemas built with `zod/mini` and with classic `zod`.
 */
export function parseWithZod<S extends $ZodType>(
  payload: FormData | URLSearchParams,
  options: ParseOptions<S> & { async?: false },
): Submission<output<S>>;
export function parseWithZod<S extends $ZodType>(
  payload: FormData | URLSearchParams,
  options: ParseOptions<S> & { async: true },
): Promise<Submission<output<S>>>;
export function parseWithZod<S extends $ZodType>(
  payload: FormData | URLSearchParams,
  { schema, async }: ParseOptions<S>,
): Submission<output<S>> | Promise<Submission<output<S>>> {
  const coerced = coerceFormValue(schema);
  type Result = ReturnType<typeof safeParse<S>>;
  const toSubmission = (result: Result) => ({
    value: result.success ? result.data : undefined,
    error: result.success ? undefined : getError(result.error.issues),
  });
  if (async) {
    return parse<output<S>, string[]>(payload, {
      resolve: async (data) =>
        toSubmission(await safeParseAsync(coerced, data)),
    });
  }
  return parse<output<S>, string[]>(payload, {
    resolve: (data) => toSubmission(safeParse(coerced, data)),
  });
}
